// Tier 7 — Composite HomeIQ Score.
// Pure function. Consumes sub-scores from each tier's engine and produces a
// weighted composite + verdict label + pillar breakdown.
//
// Vastu pillar is intentionally omitted at this stage (Tier 6 not yet wired).
// When Vastu lands, append it to PILLAR_WEIGHTS without touching consumers.

const PILLAR_WEIGHTS = {
    affordability: 0.30,
    loan:          0.20,
    market:        0.20,
    investment:    0.30,
    // vastu: 0.10  ← intentionally commented out until Tier 6 is live
};

// ── Public · compute composite ─────────────────────────────────────
// inputs: { affordability, loan, market, investment }  // each null or 0..100
// returns: { score, verdict, pillars: [{id, label, score, weight, contribution, status, included}] }
export function computeHomeIQComposite(scores) {
    const raw = {
        affordability: numericOrNull(scores?.affordability),
        loan:          numericOrNull(scores?.loan),
        market:        numericOrNull(scores?.market),
        investment:    numericOrNull(scores?.investment),
    };

    // Filter included pillars and re-normalise their weights to sum to 1.
    const includedKeys = Object.keys(raw).filter((k) => raw[k] != null);
    const totalIncludedWeight = includedKeys.reduce((s, k) => s + PILLAR_WEIGHTS[k], 0);

    let composite = 0;
    if (totalIncludedWeight > 0) {
        for (const k of includedKeys) {
            composite += (raw[k] * PILLAR_WEIGHTS[k]) / totalIncludedWeight;
        }
    }
    const score = Math.round(composite);

    const verdict = score >= 75 ? "Strong"
        : score >= 55 ? "Moderate"
        : score > 0   ? "Concerns"
        : "Pending";

    const pillars = [
        { id: "affordability", label: "Affordability" },
        { id: "loan",          label: "Loan" },
        { id: "market",        label: "Market" },
        { id: "investment",    label: "Investment" },
    ].map((p) => {
        const v = raw[p.id];
        const included = v != null;
        const weight = PILLAR_WEIGHTS[p.id];
        const renormWeight = included && totalIncludedWeight > 0 ? weight / totalIncludedWeight : 0;
        const contribution = included ? Math.round(v * renormWeight * 100) / 100 : 0;
        return {
            id: p.id,
            label: p.label,
            score: included ? v : null,
            weight,
            effectiveWeight: renormWeight,
            contribution,
            included,
            status: included ? pillarStatus(v) : "pending",
        };
    });

    return { score, verdict, pillars };
}

function numericOrNull(v) {
    if (v == null) return null;
    const n = Number(v);
    if (!isFinite(n)) return null;
    return Math.max(0, Math.min(100, n));
}

function pillarStatus(v) {
    if (v >= 75) return "strong";
    if (v >= 55) return "moderate";
    return "concerns";
}
